import React, { useState, useEffect, useRef, useContext } from 'react';
import dashboardStyles from '../Dashboard.style';
import { Table, TableBody, TableContainer, TableRow, Box, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import bulletIcon from 'assets/svgs/bullet.svg';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import PropTypes from 'prop-types';
import {
	NAME,
	TEMPLATE,
	DOT_SPACING,
	SCHEDULED,
	SHARED,
	LAST_UPDATED,
	SAVED_RPT_MSG,
	FREQUENCY,
	STATUS,
	BASIC,
	DRAFT_NAME,
	TEMPLATE_NAME,
	LAST_UPDATED_DATE,
	STATUS_LOWER,
	FREQUENCY_LOWER
} from 'constants/constant';
import { getLastRun } from 'helper/utils';
import {
	computeHeight,
	computeFontSize,
	computeWidthsForSavedTable
} from './utils/dashboard-utils';
import useScreenWidthSize from './hooks/useScreenWidthSize';
import MyTableCellHOC from './styled-components/MyTableCellHOC';
import { useSelector } from 'react-redux';
import DraftMenuList from './DraftMenuList';
import { MAX_SAVE_DRAFT } from 'constants/constant';
import FilterBy from '../components/FilterBy';
import MetaData from '../meta-data/MetaData';
import CustomPagination from './CustomPagination';
import NoReports from './NoReports';
import ReportTableHeader from './ReportTableHeader';
import { StyledLinearProgress } from '../Dashboard.style';
import NoDataPage from '../../Shared/NoDataPage';
import { isObjEmpty } from 'helper/helper';
import { UserPortalContext } from 'context/appcontext';

const fieldMapping = [
	{ name: STATUS, field: STATUS_LOWER },
	{ name: TEMPLATE, field: TEMPLATE_NAME },
	{ name: FREQUENCY, field: FREQUENCY_LOWER }
];

function SavedReportTable(props) {
	const { reportData, isSmallScreen } = props;
	const { tabValue, searchTxt, filterBy, savedReportsList } = useSelector(
		(state) => state.dashboardReducer
	);
	const target = useRef(null);
	const figmaBreakPoint = useScreenWidthSize();
	const widths = computeWidthsForSavedTable(figmaBreakPoint);
	const height = computeHeight(figmaBreakPoint);
	const fontHeight = computeFontSize(figmaBreakPoint);
	const classes = dashboardStyles({ isSmallScreen });
	const [dispAlertList, setDispAlertList] = useState(0);
	const [selectedRow, setSelectedRow] = useState(null);
	const [show, setShow] = useState(false);
	const [filteredData, setFilteredData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageCount, setPageCount] = useState(1);
	const [previewingData, setPreviewingData] = useState(reportData);
	const portalUserContext = useContext(UserPortalContext);
	const { customerType } = portalUserContext;
	const onPageChange = (event, value) => {
		setCurrentPage(value);
		const start = (value - 1) * 10;
		const end = start + 10;
		setPreviewingData(filteredData.slice(start, end));
	};

	useEffect(() => {
		let count = 0;
		if (savedReportsList.length >= MAX_SAVE_DRAFT) {
			count += 1;
		}
		setDispAlertList(count);
		setPageCount(Math.ceil(reportData.length / 10));
	}, []);

	useEffect(() => {
		const search = (element) => {
			return Object.keys(filterBy).every((key) => {
				const f = fieldMapping.filter((el) => el.name === key).map((value) => value.field)[0];
				const n = fieldMapping.filter((el) => el.name === key).map((value) => value.name)[0];
				return filterBy[n].map((itm) => itm?.toUpperCase()).includes(element[f]?.toUpperCase());
			});
		};
		if (isObjEmpty(filterBy)) {
			setFilteredData(reportData);
		}

		if (filterBy[FREQUENCY]?.includes('Once')) {
			const idx = filterBy[FREQUENCY]?.indexOf('Once');
			filterBy[FREQUENCY]?.splice(idx, 1);
			filterBy[FREQUENCY]?.push('Now');
		}

		const filterReportData = reportData.filter(search);
		setFilteredData(filterReportData);
		setPreviewingData(filterReportData.slice(0, 10));
		setPageCount(Math.ceil(filterReportData.length / 10));
		setCurrentPage(1);
	}, [filterBy, reportData]);

	const headers = [
		{ name: NAME, sortBy: DRAFT_NAME, field: DRAFT_NAME },
		{ name: TEMPLATE, field: TEMPLATE_NAME },
		{ name: LAST_UPDATED, sortBy: LAST_UPDATED_DATE, field: LAST_UPDATED_DATE },
		{ name: undefined }
	];

	const getBulletIcon = (item) => {
		if (item?.sharedWith?.viewed || item.downloadCount !== 0) {
			return;
		}
		return (
			<div style={{ alignSelf: 'center', marginTop: '-20px' }}>
				<img className={classes.bullet} src={bulletIcon} alt="bullet-icon"></img>
			</div>
		);
	};

	if (reportData.length === 0 && searchTxt.length > 0) {
		return (
			<Box className={classes.errorContainer}>
				<NoDataPage
					errorMsg={'No Results Found. Try a different keyword.'}
					isSmallScreen={isSmallScreen}
				/>
			</Box>
		);
	}

	return (
		<>
			<FilterBy />
			{savedReportsList.length >= MAX_SAVE_DRAFT && (
				<Alert variant="outlined" severity="error" className={classes.showAlertForSpaceFull}>
					<Typography style={{ fontWeight: 700, fontSize: 14 }}>
						{SAVED_RPT_MSG.STORAGE_FULL}
					</Typography>
					<Typography style={{ fontSize: 14, fontWeight: 500 }}>
						{SAVED_RPT_MSG.STORAGE_FULL_EXT}
					</Typography>
				</Alert>
			)}

			<TableContainer
				className={classes.tableContainer}
				style={{
					height:
						// Basic user account Banner is 40px, need to shorten or else pagination will not be visible
						customerType == BASIC
							? `calc(460px - ${dispAlertList * 45}px)`
							: `calc(500px - ${dispAlertList * 45}px)`
				}}>
				<Table
					ref={target}
					stickyHeader
					className={classes.table}
					height={height}
					role="grid"
					id="retort-table"
					aria-labelledby="scheduled reports table"
					aria-label="scheduled reports table">
					<ReportTableHeader
						headers={headers}
						widths={widths}
						height={height}
						fontHeight={fontHeight}
					/>
					{previewingData.length ? (
						<TableBody>
							{previewingData?.map((item, index) => (
								<TableRow
									className={`${classes.tableRow} ${
										item.status === SCHEDULED ? classes.highlightRow : null
									}`}
									key={item.draftId}
									onClick={() => {
										// getSelectedRows(item);
										setSelectedRow(item);
										setShow(true);
									}}>
									<MyTableCellHOC
										style={{ display: 'flex' }}
										width={widths[1] - DOT_SPACING}
										height={height}
										fontSize={fontHeight}
										downloadCount={item.downloadCount}>
										<div
											style={{
												textOverflow: 'ellipsis',
												overflow: 'hidden',
												whiteSpace: 'nowrap',
												maxWidth: widths[1] - DOT_SPACING,
												flexBasis: 'min-content',
												alignSelf: 'center',
												fontFamily: 'inherit'
											}}>
											{item.draftName}
										</div>
										{(item.sharedWith?.length > 0 || tabValue === SHARED) && (
											<div style={{ alignSelf: 'center' }}>
												<PeopleAltIcon alt="share-icon" className={classes.shareIcon} />
											</div>
										)}
										{getBulletIcon(item)}
									</MyTableCellHOC>
									<MyTableCellHOC
										downloadCount={item.downloadCount}
										width={widths[2]}
										height={height}
										fontSize={fontHeight}>
										<div
											style={{
												textOverflow: 'ellipsis',
												overflow: 'hidden',
												whiteSpace: 'nowrap',
												fontFamily: 'inherit'
											}}>
											{item.templateName}
										</div>
									</MyTableCellHOC>

									<MyTableCellHOC
										downloadCount={item.downloadCount}
										width={widths[4]}
										height={height}
										fontSize={fontHeight}>
										{getLastRun(item.lastUpdatedDate)}
									</MyTableCellHOC>

									<MyTableCellHOC
										style={{ height: '48px', maxHeight: '48px', border: '0px' }}
										className={`${classes.tableCell} ${
											item.downloadCount === 0 ? classes.newTableCell : classes.prevTableCell
										}`}>
										<DraftMenuList rowData={item} index={index} />
									</MyTableCellHOC>
								</TableRow>
							))}
						</TableBody>
					) : (
						<NoReports />
					)}
				</Table>
			</TableContainer>
			<Box>
				{filteredData?.length > 0 && reportData?.length > 10 && (
					<CustomPagination
						onPageChange={onPageChange}
						currentPage={currentPage}
						pageCount={pageCount}
					/>
				)}
			</Box>
			<Box style={{ position: 'fixed', width: '100%', left: 24, bottom: 24 }}>
				<Box className={classes.draftProgress}>
					<StyledLinearProgress
						variant="determinate"
						value={(savedReportsList.length / 500) * 100}
					/>
					<Typography style={{ fontSize: 14, marginTop: 8 }}>
						{savedReportsList.length} of {MAX_SAVE_DRAFT} saved{' '}
						{savedReportsList.length > 1 ? 'reports' : 'report'} used
					</Typography>
				</Box>
			</Box>
			<MetaData data={selectedRow} show={show} setShow={setShow} />
		</>
	);
}

SavedReportTable.propTypes = {
	isSmallScreen: PropTypes.bool
};

export default SavedReportTable;
